import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Images, Pencil } from "lucide-react";
import { ProductImage, primaryImageId } from "../../components/ProductImage";
import { ProductPhotos } from "./ProductPhotos";
import { ProductEditor } from "./ProductEditor";
import { money } from "../../lib/cart";
import { Checkbox } from "../../components/ui/checkbox";
import { ProductFilters } from "./ProductFilters";
import { BulkVisibilityBar } from "./BulkVisibilityBar";
import { VisibilitySwitch } from "./VisibilitySwitch";

const matches = (p, q, category, visibility, photos) =>
  p.name.toLowerCase().includes(q.trim().toLowerCase()) &&
  (category === "all" || p.category === category) &&
  (photos === "all" || !(p.images || []).length) &&
  (visibility === "all" || (visibility === "hidden" ? p.hidden || p.archived : !p.hidden && !p.archived));
const selectable = (p) => p.category === "research" && !p.archived;

export default function AdminProducts() {
  const { getToken } = useAuth();
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | product
  const [photosFor, setPhotosFor] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [visibility, setVisibility] = useState("all");
  const [photos, setPhotos] = useState("all");
  const [picked, setPicked] = useState(new Set());

  const shown = useMemo(() => products.filter((p) => matches(p, q, category, visibility, photos)), [products, q, category, visibility, photos]);
  const shownSelectable = shown.filter(selectable);
  const selected = products.filter((p) => picked.has(p.id) && selectable(p));
  const allPicked = shownSelectable.length > 0 && shownSelectable.every((p) => picked.has(p.id));
  const togglePick = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePickAll = () => setPicked((s) => {
    const n = new Set(s);
    shownSelectable.forEach((p) => (allPicked ? n.delete(p.id) : n.add(p.id)));
    return n;
  });

  const bulkApply = async (hidden) => {
    try {
      const headers = { Authorization: `Bearer ${await getToken()}` };
      const r = await api.post("/admin/products/bulk-visibility", { ids: selected.map((p) => p.id), hidden }, { headers });
      toast.success(`${r.data.matched} product${r.data.matched === 1 ? "" : "s"} ${hidden ? "hidden" : "now visible"}`);
      setPicked(new Set());
    } catch (e) {
      toast.error("Could not update visibility. Please try again.");
    }
    load();
  };

  const load = async () => {
    const token = await getToken();
    const r = await api.get("/admin/products", { headers: { Authorization: `Bearer ${token}` } });
    setProducts(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async (body) => {
    try {
      const headers = { Authorization: `Bearer ${await getToken()}` };
      if (editing === "new") await api.post("/admin/products", body, { headers });
      else await api.patch(`/admin/products/${editing.id}`, body, { headers });
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check every option has a strength or size and a price.");
    }
  };

  const archive = async (id) => {
    const token = await getToken();
    await api.delete(`/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success("Archived");
    load();
  };

  const toggleHidden = async (p) => {
    try {
      const token = await getToken();
      await api.patch(`/admin/products/${p.id}`, { hidden: !p.hidden }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(p.hidden ? "Product is now visible" : "Product is now hidden");
    } catch (e) {
      toast.error("Could not change visibility — please try again.");
    }
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <p className="text-xs uppercase tracking-[0.3em] text-[#5E6B51]">Catalog</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <h1 className="font-serif text-4xl mt-2">Products</h1>
        <Button onClick={() => setEditing("new")} className="rounded-xl bg-[#59394F] hover:bg-[#432A3B] text-white" data-testid="np-open"><Plus size={14} className="mr-1" />New product</Button>
      </div>

      <ProductFilters q={q} setQ={setQ} category={category} setCategory={setCategory}
        visibility={visibility} setVisibility={setVisibility} photos={photos} setPhotos={setPhotos} shown={shown.length} total={products.length} />
      <BulkVisibilityBar selected={selected} hiddenCount={selected.filter((p) => !shown.includes(p)).length} onApply={bulkApply} onClear={() => setPicked(new Set())} />

      <div className="bg-white rounded-2xl border border-[#A8B59A]/20 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#5E4D5A] border-b border-[#A8B59A]/20">
              <th className="p-4 w-10">
                <Checkbox checked={allPicked} onCheckedChange={togglePickAll} disabled={!shownSelectable.length}
                  aria-label="Select all research products shown" data-testid="select-all-research" />
              </th>
              <th className="p-4">Photo</th><th className="p-4">Name</th><th className="p-4">Status</th><th className="p-4">Category</th>
              <th className="p-4">Strengths / sizes · price</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-[#5E4D5A]" data-testid="products-empty">No products match these filters.</td></tr>
            )}
            {shown.map((p) => (
              <tr key={p.id} data-testid={`prod-row-${p.id}`} className="border-b border-[#A8B59A]/10">
                <td className="p-4">
                  {selectable(p) && (
                    <Checkbox checked={picked.has(p.id)} onCheckedChange={() => togglePick(p.id)}
                      aria-label={`Select ${p.name}`} data-testid={`select-${p.id}`} />
                  )}
                </td>
                <td className="p-4">
                  <button onClick={() => setPhotosFor(p)} className="flex items-center gap-2 group" data-testid={`photos-${p.id}`}>
                    <ProductImage imageId={primaryImageId(p)} variant="thumb" alt={p.name} iconSize={18} className="w-12 h-12 rounded-lg" />
                    <span className="text-xs text-[#59394F] group-hover:underline whitespace-nowrap inline-flex items-center gap-1"><Images size={12} />{p.images?.length ? `${p.images.length} · Manage photos` : "Add photos"}</span>
                  </button>
                </td>
                <td className="p-4 font-medium">{p.name}</td>
                <td className="p-4 whitespace-nowrap">
                  <VisibilitySwitch product={p} onToggle={toggleHidden} />
                </td>
                <td className="p-4 text-xs text-[#5E4D5A] whitespace-nowrap" data-testid={`category-${p.id}`}>{p.category === "skincare" ? "Skin Booster Serums" : "Research Catalog"}</td>
                <td className="p-4 text-xs" data-testid={`variants-${p.id}`}>
                  {(p.variants || []).map((v) => <div key={v.id}>{v.strength} · {money(v.price_cents)}</div>)}
                </td>
                <td className="p-4 whitespace-nowrap">
                  <button onClick={() => setEditing(p)} data-testid={`edit-${p.id}`} className="text-[#5A433B] hover:text-[#432A3B] mr-3" title="Edit"><Pencil size={14} /></button>
                  <button onClick={() => archive(p.id)} data-testid={`archive-${p.id}`} className="text-[#59394F] hover:text-[#432A3B]" title="Archive"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {photosFor && <ProductPhotos key={photosFor.id} product={photosFor} onClose={() => setPhotosFor(null)} onChanged={load} />}
      {editing && <ProductEditor key={editing === "new" ? "new" : editing.id} product={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
